import { Box, Divider, Skeleton, Typography } from '@mui/material';
import CustomNotes from 'components/shared/CustomNotes';
import dayjs from 'dayjs';
import { CompleteOperationType, StartOperationType } from 'model/alerts.model';
import { useCallback, useMemo } from 'react';
import {
  TOP_STAGE_OPTIONS,
  formatPipelineType,
  mapDealToDetailFields,
} from '../DealsDetails.utils';
import type { OwnerOption } from '../hooks/useDealDetailsData';
import { useDealNotes } from '../hooks/useDealNotes';
import { DetailRow, DetailRowProps } from './DetailRow';
import { SectionHeader } from './SectionHeader';

type DealDetailsContentProps = {
  fields: ReturnType<typeof mapDealToDetailFields>;
  dealData: any;
  loading: boolean;
  onFieldChange?: (fieldName: string, newValue: string) => void;
  ownerOptions?: OwnerOption[];
  stageOptions?: string[];
  onActivityHistoryClick?: () => void;
  completeOperation: CompleteOperationType;
  startOperation: StartOperationType;
};

export const DealDetailsContent = ({
  fields,
  dealData,
  loading,
  onFieldChange,
  ownerOptions = [],
  stageOptions = [],
  onActivityHistoryClick,
  completeOperation,
  startOperation,
}: DealDetailsContentProps) => {
  const systemInfo: DetailRowProps[] = useMemo(
    () => [
      {
        label: 'Created by',
        value: dealData?.createdBy || dealData?.createdByName || 'N/A',
        isAvatar: true,
      },
      {
        label: 'Created date',
        value:
          dealData?.createdDate || dealData?.createdAt
            ? dayjs(dealData.createdDate || dealData.createdAt).format('D MMMM, YYYY')
            : 'N/A',
      },
      {
        label: 'Last modified date',
        value:
          dealData?.lastModifiedDate || dealData?.updatedAt
            ? dayjs(dealData.lastModifiedDate || dealData.updatedAt).format('D MMMM, YYYY')
            : 'N/A',
        hasActivity: true,
      },
    ],
    [dealData],
  );

  const getFieldOptions = useCallback(
    (fieldName: string, currentValue: string) => {
      if (fieldName === 'stageName' && stageOptions.length > 0) {
        const capitalizeStage = (stage: string) => {
          return stage
            .split(' ')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
        };

        const options = stageOptions.map((stage) => ({
          value: stage,
          label: capitalizeStage(stage),
        }));

        if (
          currentValue &&
          currentValue !== 'N/A' &&
          !stageOptions.some((opt) => opt.toLowerCase() === currentValue.toLowerCase())
        ) {
          return [
            { value: currentValue.toLowerCase(), label: capitalizeStage(currentValue) },
            ...options,
          ];
        }
        return options;
      }

      if (fieldName === 'ownerName' && ownerOptions.length > 0) {
        const currentOwner = String(currentValue || '');
        if (currentOwner && !ownerOptions.find((o) => o.value === currentOwner)) {
          return [{ value: currentOwner, label: currentOwner }, ...ownerOptions];
        }
        return ownerOptions;
      }

      return undefined;
    },
    [stageOptions, ownerOptions],
  );

  const manager = useDealNotes(dealData.id, startOperation, completeOperation);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
          pb: 2,
          flex: 1,
          overflow: 'scroll',
          pr: 1,
          width: '100%',
          height: '100vh',
        }}
      >
        <SectionHeader title='Opportunity Information' />
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} variant='text' height={24} width='80%' />
        ))}

        <Divider sx={{ my: 3 }} />

        <SectionHeader title='Financial Information' />
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} variant='text' height={24} width='80%' />
        ))}

        <Divider sx={{ my: 3 }} />

        <SectionHeader title='Attribution & Tracking' />
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} variant='text' height={24} width='80%' />
        ))}

        <Divider sx={{ my: 3 }} />

        <SectionHeader title='Additional Information' />
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} variant='text' height={24} width='80%' />
        ))}

        <Divider sx={{ my: 3 }} />

        <SectionHeader title='System Information' />
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} variant='text' height={24} width='80%' />
        ))}
      </Box>
    );
  }

  if (!dealData) {
    return <Typography sx={{ p: 2 }}>No deal data available</Typography>;
  }

  const renderFieldSection = (items: any[], sectionKey: string) => {
    if (items.length === 0) {
      return (
        <Typography variant='body2' sx={{ color: 'text.disabled', pl: 4 }}>
          No {sectionKey.toLowerCase()} available
        </Typography>
      );
    }

    return items.map((item, i) => {
      const currentValue = String(item.value || '');
      const options = onFieldChange
        ? getFieldOptions(item.fieldName || '', currentValue)
        : undefined;
      let displayValue = currentValue;
      if (item.fieldName === 'stageName') {
        const dealStageName = dealData?.stageName || '';
        const stageValue = currentValue && currentValue !== 'N/A' ? currentValue : dealStageName;
        if (stageValue && stageValue !== 'N/A') {
          displayValue = stageValue.toLowerCase();
        } else {
          displayValue = TOP_STAGE_OPTIONS[0]?.toLowerCase() || '';
        }
      }
      if (item.fieldName === 'pipelineType') {
        if (currentValue && currentValue !== 'N/A') {
          displayValue = formatPipelineType(currentValue);
        }
      }

      return (
        <DetailRow
          key={`${sectionKey}-${i}`}
          {...item}
          value={displayValue}
          onChange={onFieldChange}
          options={options}
        />
      );
    });
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        pb: 2,
        flex: 1,
        overflow: 'scroll',
        pr: 1,
        width: '100%',
        height: '100vh',
      }}
    >
      <SectionHeader title='Opportunity Information' />
      {renderFieldSection(fields.opportunityInfo, 'opp')}

      <Divider sx={{ my: 3 }} />

      <SectionHeader title='Financial Information' />
      {renderFieldSection(fields.financialInfo, 'fin')}

      <Divider sx={{ my: 3 }} />

      <SectionHeader title='Attribution & Tracking' />
      {renderFieldSection(fields.attributionTracking, 'attr')}

      <Divider sx={{ my: 3 }} />

      <SectionHeader title='Additional Information' />
      {renderFieldSection(fields.additionalInfo, 'add')}

      <Divider sx={{ my: 3 }} />

      <SectionHeader title='System Information' />
      {systemInfo.map((item, i) => (
        <DetailRow key={`sys-${i}`} {...item} onActivityHistoryClick={onActivityHistoryClick} />
      ))}

      <CustomNotes manager={manager} />
    </Box>
  );
};
